import { useMemo, useState } from 'react';
import type { DocumentRenderAttachmentSummary, EntryFormAction } from '@vivport/backend/types';
import { Button } from '@/components/ui/button';
import { useDocumentRender } from '@/hooks/useContentQueries';
import { EntryFormFields } from '@/pages/details/template-chrome/EntryFormFields';
import {
  buildAddEntryInput,
  buildUpdateEntryInput,
  entryFormCanSubmit,
  initialEntryFormState,
  resolveEntryTitle,
  type AddEntryFormInput,
  type EntryFormState,
  type UpdateEntryFormInput,
} from '@/pages/details/template-chrome/entry-form-state';
import { useCaptureWrite } from '@/hooks/useCaptureWrite';
import { InlineRetryError } from '@/components/ui/inline-retry-error';
import { hasMatchingEntry } from '@/pages/details/template-chrome/entry-write-guard';
import { getDocumentRender } from '@/api/content-api';
import { Loader2 } from 'lucide-react';

export type EntryFormSubmit =
  | { mode: 'add'; title: string; input: AddEntryFormInput }
  | { mode: 'update'; title: string; entryNumber: number; input: UpdateEntryFormInput | null };

interface EntryFormActionCardProps {
  action: EntryFormAction;
  disabled: boolean;
  conversationPhotoIds: number[];
  onSubmit: (submit: EntryFormSubmit) => Promise<void>;
}

// Everything drawn here came from the server-resolved payload (the definition and, for a
// correction, the parsed entry); the card collects values and hands them to the submit path,
// which writes through templates.addEntry / updateEntry and echoes the member's line.
export function EntryFormActionCard({ action, disabled, conversationPhotoIds, onSubmit }: EntryFormActionCardProps) {
  const { payload } = action;
  const { def, prefill } = payload;
  const [state, setState] = useState<EntryFormState>(() => initialEntryFormState(def, prefill));
  const captureWrite = useCaptureWrite();
  const pending = captureWrite.pending;
  const render = useDocumentRender(payload.instanceId, { enabled: def.photos });

  const photos = useMemo<DocumentRenderAttachmentSummary[]>(() => {
    const attached = (render.data?.sections ?? [])
      .flatMap((section) => section.otherAttachments)
      .filter((item) => item.contentType === 'PHOTO');
    const seen = new Set<number>();
    const list: DocumentRenderAttachmentSummary[] = [];
    for (const item of attached) {
      if (seen.has(item.id)) continue;
      seen.add(item.id);
      list.push(item);
    }
    for (const id of conversationPhotoIds) {
      if (seen.has(id)) continue;
      seen.add(id);
      list.push({ id, code: `Photo ${id}`, contentType: 'PHOTO', title: '', obligationRollup: null, externalRefs: [] });
    }
    return list;
  }, [render.data, conversationPhotoIds]);

  const sectionId = useMemo(
    () => render.data?.sections.find((section) => section.code === payload.sectionCode)?.id ?? payload.instanceId,
    [render.data, payload.sectionCode, payload.instanceId]
  );

  const patch = (next: Partial<EntryFormState>) => {
    captureWrite.clear();
    setState((prev) => ({ ...prev, ...next }));
  };

  const submit = () => {
    const title = resolveEntryTitle(state);
    if (prefill !== null) {
      void captureWrite.run({
        label: 'templates.updateEntry',
        write: () =>
          onSubmit({
            mode: 'update',
            title,
            entryNumber: prefill.entryNumber,
            input: buildUpdateEntryInput(def, state, prefill),
          }),
        onWritten: () => undefined,
      });
      return;
    }
    const input = buildAddEntryInput(def, state);
    void captureWrite.run({
      label: 'templates.addEntry',
      write: () => onSubmit({ mode: 'add', title, input }),
      alreadyWritten: async () => {
        const fresh = await getDocumentRender(payload.instanceId);
        const freshSection = fresh.sections.find((section) => section.code === payload.sectionCode);
        return hasMatchingEntry(freshSection?.templateSection?.entries ?? [], input);
      },
      onWritten: () => undefined,
    });
  };

  const idPrefix = `entry-form-${payload.sectionCode}-${prefill?.entryNumber ?? 'new'}`;

  return (
    <div data-testid="entry-form-action-card" className="space-y-3 rounded-md border bg-background p-2.5">
      <p className="text-sm font-medium">
        {action.label}
        {payload.sectionTitle !== null ? (
          <span className="ml-1.5 text-xs font-normal text-muted-foreground">{payload.sectionTitle}</span>
        ) : null}
      </p>
      <EntryFormFields
        def={def}
        state={state}
        onChange={patch}
        sectionId={sectionId}
        photos={photos}
        existingPhotoCodes={prefill?.photoCodes ?? []}
        capture={false}
        disabled={disabled || pending}
        idPrefix={idPrefix}
        pickerKey={0}
      />
      {captureWrite.error !== null ? (
        <InlineRetryError message={captureWrite.error} onRetry={captureWrite.retry} disabled={pending} />
      ) : null}
      <div className="flex justify-end">
        <Button
          size="sm"
          className="max-sm:h-11 max-sm:w-full"
          disabled={disabled || pending || !entryFormCanSubmit(def, state)}
          onClick={() => submit()}
        >
          {pending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden /> : null}Save
        </Button>
      </div>
    </div>
  );
}
